/**
 * @author Maxime Mustarda <maxime@inarix.com>
 * @file LicencesTable.tsx
 * @desc Created on Mon Apr 28 2025 20:44:52
 * @copyright All rights reserved @ Inarix
 */

import { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { Box, Button, Link, Modal, ThemeProvider } from "@mui/material";
import { DataGridPro, GridColDef, GridDeleteIcon } from "@mui/x-data-grid-pro";
import { Add } from "@mui/icons-material";
import themeInarix from "../StylesMuiInarix";
import { selectLocale } from "../../redux/slices/layout";
import {
  focusedLicense,
  selectLicencesAndOwners,
  selectLicenseLoading,
  selectModalLicense,
  selectOnlyUnassigned,
} from "../../redux/slices/licenses/licenseList";
import { licenseIsInfinite, renderLicenseCredits } from "./LicenceCredits";
import {
  ExtendedLicense,
  LicenseStatus,
} from "../../redux/actions/licenses/licenceTypes";
import { unlinkLicenseUser } from "../../redux/actions/licenses/licenseListAsyncActions";
import { LicensePacksModal } from "./LicensePacksModal";

const columns = (t: TFunction, dispatch: ThunkDispatch<any, any, any>) =>
  [
    {
      field: "username",
      headerName: t("Data.table.Username"),
      width: 300,
      sortable: true,
      type: "string",
      renderCell(params) {
        return params.value ? <>{params.value}</> : <Link href={"/users"} />;
      },
    },
    {
      field: "siteName",
      headerName: t("Dashboard.activityTable.site"),
      width: 300,
      sortable: false,
      type: "string",
      renderCell(params) {
        return <>{params.value}</>;
      },
    },
    {
      field: "balance",
      headerName: t("Users_tables.columns_table_one.balance"),
      width: 200,
      sortable: true,
      type: "string",
      renderCell(params) {
        return renderLicenseCredits(
          [params.row],
          t("licenses.addLicense"),
          t("licenses.expired")
        );
      },
    },
    {
      field: "expiryDate",
      headerName: t("licenses.expiration"),
      width: 200,
      sortable: true,
      type: "string",
      renderCell(params) {
        return <>{new Date(params.value).toLocaleDateString()}</>;
      },
    },
    {
      field: "id",
      headerName: t("licenses.unlinkUser"),
      width: 150,
      sortable: false,
      renderCell(params) {
        return (
          <Button
            disabled={params.row.statusConstantId > LicenseStatus.assigned}
            color="error"
            onClick={() => dispatch(unlinkLicenseUser(params.value))}
          >
            <GridDeleteIcon />
          </Button>
        );
      },
    },
    {
      field: "",
      headerName: t("licenses.addPack"),
      width: 150,
      sortable: false,
      renderCell(params) {
        return licenseIsInfinite(params.row) ||
          !(params.row as any).hasPacks ? null : (
          <Button
            color="info"
            onClick={() => dispatch(focusedLicense(params.row.id))}
          >
            <Add />
          </Button>
        );
      },
    },
  ] satisfies GridColDef<ExtendedLicense>[];

export const LicencesTable: FC = () => {
  const locale = useSelector(selectLocale);
  const licenses = useSelector(selectLicencesAndOwners);
  const unassignedOnly = useSelector(selectOnlyUnassigned);
  const loading = useSelector(selectLicenseLoading);
  const modalLicense = useSelector(selectModalLicense);
  const [t] = useTranslation("global");
  const dispatch = useDispatch();

  return (
    <ThemeProvider theme={themeInarix(locale)}>
      <div className="container-licenses-table">
        <Box sx={{ height: "67vh", typography: "body1" }}>
          <DataGridPro
            rows={
              unassignedOnly
                ? licenses.filter((l) => !("username" in l))
                : licenses
            }
            columns={columns(t, dispatch)}
            rowHeight={70}
            pagination
            disableColumnSelector={true}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            loading={loading}
            pageSizeOptions={[25, 50, 100]}
          />
        </Box>
      </div>
      <Modal open={!!modalLicense} onClose={() => dispatch(focusedLicense(""))}>
        <LicensePacksModal />
      </Modal>
    </ThemeProvider>
  );
};
